import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mail, Lock, Key, ArrowLeft, 
  ShieldCheck, ArrowRight, Info,
  CheckCircle2, Terminal
} from 'lucide-react';
import toast from 'react-hot-toast';
import { authApi } from '../services/api';

export const ForgotPassword = () => {
  const [step, setStep] = useState(1); // 1: Email, 2: Code & New Password
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [formData, setFormData] = useState({ code: '', newPassword: '' });
  const navigate = useNavigate();

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await authApi.forgotPassword(email);
      toast.success('Código enviado com sucesso!');
      setStep(2);
    } catch (err: any) {
      toast.error(err.message || 'Erro ao solicitar código');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.code.length !== 6) {
      toast.error('O código deve ter 6 dígitos');
      return;
    }
    setLoading(true);
    try {
      await authApi.resetPassword({ email, ...formData });
      toast.success('Senha alterada com sucesso!');
      navigate('/login');
    } catch (err: any) {
      toast.error(err.message || 'Código inválido');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#0B0C1E] text-[#e2e8f0] font-sans antialiased selection:bg-[#52C47C] selection:text-[#0B0C1E] flex flex-col lg:flex-row overflow-x-hidden relative">
      
      {/* Structural Grid Lines */}
      <div className="fixed inset-0 pointer-events-none z-0 flex justify-between px-4 sm:px-12 opacity-[0.03]">
        <div className="w-[1px] h-full bg-white" />
        <div className="w-[1px] h-full bg-white" />
        <div className="w-[1px] h-full bg-white" />
        <div className="w-[1px] h-full bg-white" />
      </div>

      {/* Glow futurista */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#52C47C]/5 blur-[160px] pointer-events-none" />

      {/* Coluna Esquerda: Branding & Segurança (Desktop) */}
      <div className="hidden lg:flex lg:w-1/2 relative flex-col justify-between p-12 xl:p-16 border-r border-white/10 z-10">
        <div>
          <Link to="/" className="inline-flex items-center gap-3 group">
            <img 
              src="https://meupontodevenda.com/cdn/logo-white.svg" 
              alt="MPV — Meu Ponto de Venda" 
              className="h-7 w-auto object-contain transition-transform group-hover:scale-95"
            />
            <span className="font-mono text-[10px] tracking-widest text-[#8087A8] border-l border-white/10 pl-3">
              SISTEMA v4.0
            </span>
          </Link>
        </div>

        <div className="max-w-md my-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 border border-[#52C47C]/30 bg-[#52C47C]/10 font-mono text-[11px] text-[#52C47C] tracking-widest uppercase mb-6">
            <ShieldCheck className="w-3.5 h-3.5" />
            <span>RECUPERAÇÃO SEGURA</span>
          </div>

          <motion.h2 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl font-light text-white leading-[1.15] mb-6 tracking-tight"
          >
            Recupere o seu acesso de forma <span className="font-normal italic text-[#52C47C]">rápida e protegida.</span>
          </motion.h2>

          <p className="text-slate-300 text-sm font-light leading-relaxed mb-8">
            Os dados comerciais da sua empresa permanecem encriptados e seguros. Valide a sua identidade através do código de verificação enviado para o seu e-mail institucional.
          </p>

          <div className="p-4 bg-[#12142B] border border-white/10 font-mono text-xs text-slate-400 space-y-2">
            <div className="text-white font-bold uppercase tracking-wider flex items-center gap-2">
              <Terminal className="w-3.5 h-3.5 text-[#52C47C]" />
              <span>Protocolo de Redefinição</span>
            </div>
            <p className="font-sans font-light text-slate-400 text-xs">
              1. Solicitação do código via e-mail registado.<br />
              2. Validação do código de 6 dígitos.<br />
              3. Definição da nova palavra-passe com encriptação imediata.
            </p>
          </div>
        </div>

        <div className="pt-8 border-t border-white/10 font-mono text-[10px] text-slate-500 tracking-wider flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#52C47C]" />
            <span>SISTEMA OPERACIONAL COMERCIAL MOÇAMBIQUE</span>
          </div>
          <span>© {new Date().getFullYear()} MPV</span>
        </div>
      </div>

      {/* Coluna Direita: Formulários */}
      <div className="flex-1 flex flex-col justify-between p-6 sm:p-12 relative z-10 min-h-screen">
        
        {/* Mobile Header Logo */}
        <div className="lg:hidden flex items-center justify-between pb-6 border-b border-white/10 mb-6">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="https://meupontodevenda.com/cdn/logo-white.svg" 
              alt="MPV — Meu Ponto de Venda" 
              className="h-6 w-auto object-contain"
            />
          </Link>
          <span className="font-mono text-[10px] text-[#52C47C] border border-[#52C47C]/30 px-2 py-0.5 uppercase">
            v4.0
          </span>
        </div>

        <div className="my-auto flex items-center justify-center">
          <AnimatePresence mode="wait">
            {step === 1 ? (
              /* PASSO 1: Solicitar Código */
              <motion.div 
                key="step1"
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                className="w-full max-w-md"
              >
                <div className="bg-[#12142B] border border-white/10 p-8 sm:p-10 shadow-2xl relative">
                  
                  <Link to="/login" className="inline-flex items-center gap-2 font-mono text-xs text-[#52C47C] hover:underline mb-8 uppercase">
                    <ArrowLeft size={14} /> Voltar ao Login
                  </Link>

                  <div className="mb-8 text-left">
                    <span className="font-mono text-[10px] text-[#52C47C] tracking-widest uppercase block mb-2">
                      // RECUPERAÇÃO DE CREDENCIAIS
                    </span>
                    <h1 className="text-3xl font-light text-white tracking-tight mb-2">
                      Esqueceu a senha?
                    </h1>
                    <p className="text-slate-400 text-xs font-light">
                      Introduza o seu e-mail registado para receber um código de validação de 6 dígitos.
                    </p>
                  </div>

                  <form onSubmit={handleRequestCode} className="space-y-5">
                    <div>
                      <label className="block font-mono text-xs text-slate-400 uppercase tracking-wider mb-2">
                        E-mail de Acesso
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                          <Mail className="h-4 w-4 text-slate-500" />
                        </div>
                        <input
                          type="email"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="block w-full pl-10 pr-4 py-3 bg-[#0B0C1E] border border-white/10 text-white placeholder-slate-600 focus:border-[#52C47C] focus:ring-1 focus:ring-[#52C47C]/30 transition-all outline-none font-sans text-sm"
                          placeholder="seu@empresa.co.mz"
                        />
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-3.5 px-4 bg-[#52C47C] hover:bg-white text-[#0B0C1E] font-mono font-bold text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2 group shadow-lg shadow-[#52C47C]/15 disabled:opacity-50 mt-6"
                    >
                      {loading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#0B0C1E] border-t-transparent"></div>
                      ) : (
                        <>
                          <span>Enviar Código de Acesso</span>
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </motion.div>
            ) : (
              /* PASSO 2: Código e Nova Senha */
              <motion.div 
                key="step2"
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                className="w-full max-w-md"
              >
                <div className="bg-[#12142B] border border-white/10 p-8 sm:p-10 shadow-2xl relative">
                  
                  <div className="mb-8 text-left">
                    <span className="font-mono text-[10px] text-[#52C47C] tracking-widest uppercase block mb-2">
                      // REDEFINIR PALAVRA-PASSE
                    </span>
                    <h3 className="text-3xl font-light text-white tracking-tight mb-2">
                      Nova Palavra-passe
                    </h3>
                    <p className="text-slate-400 text-xs font-light">
                      Introduza o código de 6 dígitos enviado para o seu e-mail e defina a nova senha.
                    </p>
                  </div>

                  <form onSubmit={handleResetPassword} className="space-y-5">
                    <div>
                      <label className="block font-mono text-xs text-slate-400 uppercase tracking-wider mb-2">
                        Código de Validação (6 dígitos)
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                          <Key className="h-4 w-4 text-slate-500" />
                        </div>
                        <input
                          type="text"
                          required
                          maxLength={6}
                          value={formData.code}
                          onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                          className="block w-full pl-10 pr-4 py-3 bg-[#0B0C1E] border border-white/10 text-[#52C47C] placeholder-slate-600 focus:border-[#52C47C] focus:ring-1 focus:ring-[#52C47C]/30 outline-none transition-all font-mono text-center text-xl tracking-[0.5em]"
                          placeholder="000000"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block font-mono text-xs text-slate-400 uppercase tracking-wider mb-2">
                        Nova Palavra-passe
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                          <Lock className="h-4 w-4 text-slate-500" />
                        </div>
                        <input
                          type="password"
                          required
                          value={formData.newPassword}
                          onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                          className="block w-full pl-10 pr-4 py-3 bg-[#0B0C1E] border border-white/10 text-white placeholder-slate-600 focus:border-[#52C47C] focus:ring-1 focus:ring-[#52C47C]/30 outline-none transition-all font-sans text-sm"
                          placeholder="••••••••"
                        />
                      </div>
                    </div>

                    <div className="p-4 bg-[#0B0C1E] border border-white/10 flex gap-3 items-start">
                      <Info size={16} className="text-[#52C47C] shrink-0 mt-0.5" />
                      <p className="text-[11px] text-slate-400 leading-relaxed font-light">
                        Certifique-se de escolher uma senha com pelo menos 8 caracteres para assegurar a proteção da conta.
                      </p>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-3.5 px-4 bg-[#52C47C] hover:bg-white text-[#0B0C1E] font-mono font-bold text-xs uppercase tracking-widest transition-all disabled:opacity-50 shadow-lg shadow-[#52C47C]/15"
                    >
                      {loading ? "A redefinir..." : "Redefinir Senha"}
                    </button>
                  </form>

                  <button
                    onClick={() => setStep(1)}
                    className="mt-6 w-full text-xs font-mono text-slate-500 hover:text-white transition-colors flex items-center justify-center gap-2 uppercase"
                  >
                    <ArrowLeft size={14} /> Não recebeu o código? Tentar novamente
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Mobile Footer */}
        <div className="lg:hidden text-center font-mono text-[10px] text-slate-500 pt-6">
          © {new Date().getFullYear()} MPV — MEU PONTO DE VENDA
        </div>

      </div>

    </div>
  );
};

export default ForgotPassword;